import React, { useEffect, useState } from "react";
import { useRepairContext } from "../contexts/RepairContext";
import useFormHook from "../hooks/useForm";
import Input from "./form/Input";
import Select from "./form/Select";
import Textarea from "./form/Textarea";
import Button from "./form/Button";
import dot from "dot-object";
import { useMutation } from "@apollo/react-hooks";
import { INSERT_COST_ESTIMATE, UPDATE_REPAIR } from "../graphql/queries";
import { repairArticles, repairArticleToTitle, repairArticleToPrice } from "../helpers/repairArticles";
import { upsellArticles, upsellToTitle, upsellArticleToPrice } from "../helpers/upsellArticles";
import { disableExperimentalFragmentVariables } from "graphql-tag";
import { sumLineitemsWithoutDiscount } from "../helpers/functions";
import { useUserContext } from "src/contexts/UserContext";

const RepairPieceToLineItem = (props) => {
  const { id, setParentValue, isNew, deleteAble, deleteFn, index, currency } = props;
  const { model = "", serialNumber = "", damageDescription = "" } = props;

  const { formValues, setFormValues, register, unregister, setValue } = useFormHook();

  const RegisterFormProps = { register, unregister, setValue, formValues };
  const BaseRequiredProps = {
    errorMessage: "Dies ist ein Pflichtfeld",
    required: true,
    ...RegisterFormProps,
  };

  useEffect(() => {
    setParentValue(`${id}.articleNumber`, formValues.articleNumber);
    setParentValue(`${id}.headline`, formValues.headline);
    setParentValue(`${id}.description`, formValues.description);
    setParentValue(`${id}.price`, formValues.price);
  }, [formValues]);

  useEffect(() => {
    if (!currency || !formValues.articleNumber) return;
    const articlePrice = repairArticleToPrice[formValues.articleNumber][currency];
    setValue("price", articlePrice);
  }, [formValues.articleNumber, currency]);

  useEffect(() => {
    const articleTitle = repairArticleToTitle[formValues.articleNumber];
    const description = articleTitle ? `${articleTitle}\n${damageDescription}` : damageDescription;
    setValue("description", description);
  }, [formValues.articleNumber]);

  return (
    <div className="p-4 mb-4 border-2 border-gray-400">
      <div className="flex justify-between">
        <label className="my-2 text-xl font-semibold">Winkelstück {index + 1}</label>
        {deleteAble && (
          <button
            className="p-1 ml-auto text-3xl font-semibold leading-none text-black bg-transparent border-0 opacity-5"
            onClick={deleteFn}
          >
            <span className="block w-6 h-6 text-3xl text-black bg-transparent opacity-5">×</span>
          </button>
        )}
      </div>
      <div className="flex flex-wrap -mx-4">
        <Input
          className="w-full px-4"
          id="headline"
          label="Winkelstück Bezeichnung"
          {...BaseRequiredProps}
          defaultValue={isNew ? "" : `${model} | SN: ${serialNumber}`}
        />
        <Select
          className="w-1/2 px-4"
          options={repairArticles}
          id="articleNumber"
          label="Artikelnummer"
          placeholder="Reparaturart auswählen"
          {...BaseRequiredProps}
        />
        <Input
          className="w-1/2 px-4"
          id="price"
          label="Preis (in Cent)"
          pattern="[0-9]*"
          {...BaseRequiredProps}
          value={formValues.price || "0"}
        />
        <Textarea
          className="w-full px-4"
          id="description"
          label="Schadensbeschreibung"
          {...BaseRequiredProps}
          defaultValue={damageDescription}
          value={formValues.description}
        />
      </div>
    </div>
  );
};

const ArticleToLineItem = (props) => {
  const { id, setParentValue, deleteAble, deleteFn, index } = props;
  const { model, serialNumber, productType, damageDescription } = props;

  const { formValues, setFormValues, register, unregister, setValue } = useFormHook();

  const RegisterFormProps = { register, unregister, setValue, formValues };
  const BaseRequiredProps = {
    errorMessage: "Dies ist ein Pflichtfeld",
    required: true,
    ...RegisterFormProps,
  };

  useEffect(() => {
    setParentValue(`${id}.articleNumber`, formValues.articleNumber);
    setParentValue(`${id}.headline`, upsellToTitle[formValues.articleNumber]);
    setParentValue(`${id}.price`, formValues.price);
    setParentValue(`${id}.description`, "Optional:\n" + formValues.description);
  }, [formValues]);

  useEffect(() => {
    setValue("price", upsellArticleToPrice[formValues.articleNumber]);
  }, [formValues.articleNumber]);

  return (
    <div className="p-4 mb-4 border-2 border-gray-400">
      <div className="flex justify-between">
        <label className="my-2 text-xl font-semibold">Optionales Produkt {index + 1}</label>
        {deleteAble && (
          <button
            className="p-1 ml-auto text-3xl font-semibold leading-none text-black bg-transparent border-0 opacity-5"
            onClick={deleteFn}
          >
            <span className="block w-6 h-6 text-3xl text-black bg-transparent opacity-5">×</span>
          </button>
        )}
      </div>
      <div className="flex flex-wrap -mx-4">
        <Select
          className="w-1/2 px-4"
          options={upsellArticles}
          id={`articleNumber`}
          label="Artikelnummer"
          placeholder="Artikel auswählen"
          {...BaseRequiredProps}
        />
        <Input
          className="w-1/2 px-4"
          id={`price`}
          label="Preis (in Cent)"
          pattern="[0-9]*"
          {...BaseRequiredProps}
          value={formValues.price || "0"}
        />
        <Textarea className="w-full px-4" id={`description`} label="Beschreibung" {...BaseRequiredProps} />
      </div>
    </div>
  );
};

const CreateCostEstimate = (props) => {
  const { refetchSingle } = useRepairContext();
  const { isAdmin } = useUserContext();

  const [insertCostEstimate, { error: insertCostEstimateError }] = useMutation(INSERT_COST_ESTIMATE);

  const [updateRepair, { error: updateRepairError }] = useMutation(UPDATE_REPAIR);

  const { formValues, setFormValues, register, unregister, setValue } = useFormHook();
  const RegisterFormProps = { register, unregister, setValue, formValues };
  const BaseRequiredProps = {
    errorMessage: "Dies ist ein Pflichtfeld",
    required: true,
    ...RegisterFormProps,
  };

  const [optionalProductCount, setOptionalProductCount] = useState(0);
  const [optionalHandPieceCount, setOptionalHandPieceCount] = useState(0);

  const toCostEstimate = () => {
    const data = dot.object(JSON.parse(JSON.stringify(formValues)));
    if (!data.lineItems) return undefined;
    delete data.discountSelection;
    data.lineItems.map((lineItem) => {
      lineItem.accepted = true;
    });
    if (data.optionalProducts) {
      data.optionalProducts.map((lineItem) => {
        lineItem.accepted = false;
      });
      data.lineItems = [...data.lineItems, ...data.optionalProducts];
      delete data.optionalProducts;
    }
    data.lineItems.map((lineItem) => {
      lineItem.price = Number.parseInt(lineItem.price);
    });
    data.lineItems = [
      ...data.lineItems,
      {
        articleNumber: "S10001",
        headline: "Verpackung und Versand",
        description: "UPS",
        price: props.user.country === "DE" ? 900 : 2800,
        mandatory: true,
      },
    ];

    if (data.discountFlat) {
      data.lineItems = [
        ...data.lineItems,
        {
          articleNumber: "W13000",
          headline: "Fester Rabatt",
          price: data.discountFlat * -1,
          mandatory: true,
        },
      ];
    }
    if (data.discountPercent) {
      const sum = sumLineitemsWithoutDiscount(data.lineItems);
      const discountValue = sum * (data.discountPercent / 100);
      data.lineItems = [
        ...data.lineItems,
        {
          articleNumber: "W13001",
          headline: `Prozentualer Rabatt (${data.discountPercent}%)`,
          price: discountValue * -1,
          mandatory: true,
        },
      ];
    }
    const { id: repairId } = props;
    const costEstimate = {
      repairId,
      ...data,
      taxRate: data.taxRate,
    };
    return costEstimate;
  };

  useEffect(() => {
    props.setTemporaryEstimate({ ...toCostEstimate(), createdAt: new Date().toISOString() });
  }, [formValues]);

  const Form = (
    <div className="w-full mx-auto max-w-screen-2xl">
      <form
        id="CreateCostEstimate"
        onBlur={(e) => {
          props.setEstimateValid(e.currentTarget.checkValidity());
        }}
        onSubmit={async (e) => {
          e.preventDefault();
          const variables = toCostEstimate();
          await insertCostEstimate({ variables });
          await updateRepair({
            variables: {
              id: props.id,
              set: { status: "cost_estimate_available" },
            },
          });
          refetchSingle(props.id);
        }}
      >
        <div className="flex flex-wrap -mx-4">
          <Select
            className="w-1/2 px-4"
            options={[
              { label: "EUR", value: "EUR" },
              { label: "CHF", value: "CHF" },
            ]}
            id="currency"
            label="Währung"
            placeholder="Währung auswählen"
            {...BaseRequiredProps}
            defaultValue="EUR"
          />
          <Input
            className="w-1/2 px-4"
            id="taxRate"
            label="Steuersatz (ohne Prozentzeichen)"
            pattern="^\d*\.?\d*$"
            {...BaseRequiredProps}
            defaultValue={props.user.country === "DE" ? "19" : "7.7"}
          />
          <Select
            className="w-1/2 px-4"
            options={[
              { label: "Fester Rabatt", value: "flat" },
              { label: "Prozentualer Rabatt", value: "percent" },
            ]}
            id="discountSelection"
            label="Rabattart"
            placeholder="Rabattart auswählen"
            {...RegisterFormProps}
          />
          {formValues.discountSelection === "flat" && (
            <Input
              className="w-1/2 px-4"
              id="discountFlat"
              label="Fester Rabatt (in Cent)"
              pattern="[0-9]*"
              {...RegisterFormProps}
            />
          )}
          {formValues.discountSelection === "percent" && (
            <Input
              className="w-1/2 px-4"
              id="discountPercent"
              label="Prozentualer Rabatt (ohne Prozentzeichen)"
              pattern="[0-9]*"
              {...RegisterFormProps}
            />
          )}
        </div>
        {props.repairPieces.map((repairPiece, index) => (
          <RepairPieceToLineItem
            key={repairPiece.id}
            index={index}
            {...repairPiece}
            id={`lineItems[${index}]`}
            setParentValue={setValue}
            currency={formValues.currency}
          />
        ))}
        {[...Array(optionalHandPieceCount)].map((_, index) => {
          const deleteFn = () => setOptionalHandPieceCount(optionalHandPieceCount - 1);
          const deleteAble = optionalHandPieceCount - 1 === index;
          const lineItemIndex = props.repairPieces.length + index;
          return (
            <RepairPieceToLineItem
              key={index}
              index={lineItemIndex}
              deleteAble={deleteAble}
              deleteFn={deleteFn}
              isNew={true}
              id={`lineItems[${lineItemIndex}]`}
              setParentValue={setValue}
              currency={formValues.currency}
            />
          );
        })}
        {[...Array(optionalProductCount)].map((_, index) => {
          const deleteFn = () => setOptionalProductCount(optionalProductCount - 1);
          const deleteAble = optionalProductCount - 1 === index;
          return (
            <ArticleToLineItem
              key={index}
              id={`optionalProducts[${index}]`}
              setParentValue={setValue}
              deleteAble={deleteAble}
              deleteFn={deleteFn}
              index={index}
            />
          );
        })}
        <div className="mt-3 mb-2">
          <button
            type="button"
            className="block w-full p-2 font-bold text-center bg-gray-300 hover:bg-gray-400"
            onClick={() => setOptionalHandPieceCount(optionalHandPieceCount + 1)}
          >
            Winkelstück hinzufügen
          </button>
        </div>
        <div className="mb-10">
          <button
            type="button"
            className="block w-full p-2 font-bold text-center bg-gray-300 hover:bg-gray-400"
            onClick={() => setOptionalProductCount(optionalProductCount + 1)}
          >
            Optionales Produkt hinzufügen
          </button>
        </div>
        <div className="flex justify-end">
          <Button type="submit" className="">
            Kostenvoranschlag erstellen
          </Button>
        </div>
        {insertCostEstimateError && (
          <div className="flex justify-end text-red-400">{insertCostEstimateError.message}</div>
        )}
        {updateRepairError && <div className="flex justify-end text-red-400">{updateRepairError.message}</div>}
      </form>
    </div>
  );
  return (
    <div className={props.hidden ? "hidden" : "visible"}>
      <div className="flex justify-between">
        <h3 className="block py-4 text-xl font-semibold border-b border-gray-300 border-solid">
          Kostenvoranschlag erstellen
        </h3>
        {isAdmin && (
          <Button className="print-hide" onClick={() => props.handlePrint && props.handlePrint()}>
            Reparatur drucken
          </Button>
        )}
      </div>
      {Form}
    </div>
  );
};

export default CreateCostEstimate;
